import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { StepHandle, StepProps } from '../BusinessProfileSetupPage';
import { businessProfileSetupGateway } from '../../services/BusinessProfileSetupGateway';
import { formatZipDisplay, zipDigitsFromInput } from '../../../../shared/formatting';
import { US_STATES } from '../../validation';
import { ApiError } from '../../../identity/types';

interface FormState {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  county: string;
  mailingSameAsBilling: boolean;
  mailingAddressLine1: string;
  mailingAddressLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  mailingCountry: string;
  mailingCounty: string;
}

const EMPTY: FormState = {
  addressLine1: '', addressLine2: '', city: '', state: '', zip: '', country: 'US', county: '',
  mailingSameAsBilling: true, mailingAddressLine1: '', mailingAddressLine2: '', mailingCity: '',
  mailingState: '', mailingZip: '', mailingCountry: 'US', mailingCounty: '',
};

function validate(form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.addressLine1.trim().length < 3) errors.addressLine1 = 'Enter a physical street address (3–100 characters).';
  if (form.city.trim().length < 2) errors.city = 'Enter a city name (2–60 characters).';
  if (!form.state) errors.state = 'Select a state.';
  if (!/^\d{5}(-\d{4})?$/.test(form.zip)) errors.zip = 'Enter a 5-digit ZIP or ZIP+4, e.g. 91367 or 91367-1234.';
  if (!form.country) errors.country = 'Select a country.';
  if (!form.mailingSameAsBilling) {
    if (form.mailingAddressLine1.trim().length < 3) errors.mailingAddressLine1 = 'Enter a physical street address (3–100 characters).';
    if (form.mailingCity.trim().length < 2) errors.mailingCity = 'Enter a city name (2–60 characters).';
    if (!form.mailingState) errors.mailingState = 'Select a state.';
    if (!/^\d{5}(-\d{4})?$/.test(form.mailingZip)) errors.mailingZip = 'Enter a 5-digit ZIP or ZIP+4.';
    if (!form.mailingCountry) errors.mailingCountry = 'Select a country.';
  }
  return errors;
}

export const AddressStep = forwardRef<StepHandle, StepProps>(function AddressStep({ markStepComplete, profile, readOnly }, ref) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [tab, setTab] = useState<'billing' | 'mailing'>('billing');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // EPN-37 unsaved-changes correction: see BasicInfoStep's own identical comment on this pattern.
  const baselineRef = useRef<FormState>(EMPTY);

  useEffect(() => {
    const info = profile?.address;
    if (!info) return;
    const d = info.draft;
    const loaded: FormState = {
      addressLine1: d?.addressLine1 ?? info.addressLine1 ?? '',
      addressLine2: d?.addressLine2 ?? info.addressLine2 ?? '',
      city: d?.city ?? info.city ?? '',
      state: d?.state ?? info.state ?? '',
      zip: d?.zip ?? info.zip ?? '',
      country: d?.country ?? info.country ?? 'US',
      county: d?.county ?? info.county ?? '',
      mailingSameAsBilling: d?.mailingSameAsBilling ?? info.mailingSameAsBilling ?? true,
      mailingAddressLine1: d?.mailingAddressLine1 ?? info.mailingAddressLine1 ?? '',
      mailingAddressLine2: d?.mailingAddressLine2 ?? info.mailingAddressLine2 ?? '',
      mailingCity: d?.mailingCity ?? info.mailingCity ?? '',
      mailingState: d?.mailingState ?? info.mailingState ?? '',
      mailingZip: d?.mailingZip ?? info.mailingZip ?? '',
      mailingCountry: d?.mailingCountry ?? info.mailingCountry ?? 'US',
      mailingCounty: d?.mailingCounty ?? info.mailingCounty ?? '',
    };
    setForm(loaded);
    baselineRef.current = loaded;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field: keyof FormState, value: string | boolean) => setForm((prev) => ({ ...prev, [field]: value }));

  const toRequest = () => ({
    addressLine1: form.addressLine1.trim(),
    addressLine2: form.addressLine2.trim() || null,
    city: form.city.trim(),
    state: form.state,
    zip: form.zip,
    country: form.country,
    county: form.county.trim() || null,
    mailingSameAsBilling: form.mailingSameAsBilling,
    mailingAddressLine1: form.mailingSameAsBilling ? null : form.mailingAddressLine1.trim(),
    mailingAddressLine2: form.mailingSameAsBilling ? null : form.mailingAddressLine2.trim() || null,
    mailingCity: form.mailingSameAsBilling ? null : form.mailingCity.trim(),
    mailingState: form.mailingSameAsBilling ? null : form.mailingState,
    mailingZip: form.mailingSameAsBilling ? null : form.mailingZip,
    mailingCountry: form.mailingSameAsBilling ? null : form.mailingCountry,
    mailingCounty: form.mailingSameAsBilling ? null : form.mailingCounty.trim() || null,
  });

  /** Shared by saveAndContinue (setup mode) and commitEdits (admin-edit's Save Changes) — see
   * BasicInfoStep's own identical comment on this pattern. */
  const commit = async () => {
    const validationErrors = validate(form);
    setErrors(validationErrors);
    setShowErrors(true);
    if (Object.keys(validationErrors).length > 0) {
      if (Object.keys(validationErrors).some((k) => k.startsWith('mailing'))) setTab('mailing');
      throw new Error('validation failed');
    }

    setApiError(null);
    try {
      await businessProfileSetupGateway.saveAddressAndContinue(toRequest());
      markStepComplete('address');
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not save this step. Please try again.');
      throw err;
    }
  };

  useImperativeHandle(ref, () => ({
    saveAndContinue: commit,
    async saveAndExit() {
      setApiError(null);
      try {
        await businessProfileSetupGateway.saveAddressDraft(toRequest());
      } catch (err) {
        setApiError(err instanceof ApiError ? err.message : 'Could not save your draft. Please try again.');
      }
    },
    commitEdits: commit,
    isDirty: () => JSON.stringify(form) !== JSON.stringify(baselineRef.current),
  }));

  const err = (field: string) => (showErrors ? errors[field] : undefined);

  // EPN-37 Business Address correction: "already exists" is judged against the loaded BASELINE, not the
  // live, possibly-just-unchecked form — unchecking the checkbox this session reveals the tab immediately
  // (see the Billing Address checkbox below) but should still read "Add Mailing Address" until a real
  // separate address has actually been saved once, exactly as the approved UX specifies.
  const hasSavedMailingAddress = !baselineRef.current.mailingSameAsBilling && baselineRef.current.mailingAddressLine1.trim() !== '';

  return (
    <div>
      <div className="bps-tab-nav">
        <button type="button" className={`bps-tab${tab === 'billing' ? ' active' : ''}`} onClick={() => setTab('billing')}>
          Billing Address
        </button>
        {/* EPN-37 correction: this tab is now driven ENTIRELY by the "Mailing address is the same as
            billing address" checkbox below (and its own copy on this tab, once shown) — there is no
            separate "+ Add Mailing Address" call-to-action control anymore, so nothing actionable-looking
            is ever left showing in View mode when the checkbox says the mailing address is the same. */}
        {!form.mailingSameAsBilling && (
          <button type="button" className={`bps-tab${tab === 'mailing' ? ' active' : ''}`} onClick={() => setTab('mailing')}>
            {hasSavedMailingAddress ? 'Mailing Address' : 'Add Mailing Address'}
          </button>
        )}
      </div>

      {tab === 'billing' && (
        <div>
          <div className="bp-grid">
            <div className="bp-form-group span-2">
              <label htmlFor="bps-address1">
                Address Line 1 <span className="req">*</span>
              </label>
              <input
                id="bps-address1"
                className={`bp-input${err('addressLine1') ? ' invalid' : ''}`}
                value={form.addressLine1}
                maxLength={100}
                placeholder="e.g. 21550 Oxnard St."
                disabled={readOnly}
                onChange={(e) => set('addressLine1', e.target.value)}
              />
              {err('addressLine1') && <div className="error">{err('addressLine1')}</div>}
            </div>
            <div className="bp-form-group span-2">
              <label htmlFor="bps-address2">Address Line 2</label>
              <input id="bps-address2" className="bp-input" value={form.addressLine2} maxLength={100} placeholder="e.g. Suite 500" disabled={readOnly} onChange={(e) => set('addressLine2', e.target.value)} />
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-city">
                City <span className="req">*</span>
              </label>
              <input id="bps-city" className={`bp-input${err('city') ? ' invalid' : ''}`} value={form.city} maxLength={60} disabled={readOnly} onChange={(e) => set('city', e.target.value)} />
              {err('city') && <div className="error">{err('city')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-state">
                State <span className="req">*</span>
              </label>
              <select id="bps-state" className={`bp-select${err('state') ? ' invalid' : ''}`} value={form.state} disabled={readOnly} onChange={(e) => set('state', e.target.value)}>
                <option value="">-- Select --</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {err('state') && <div className="error">{err('state')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-zip">
                ZIP Code <span className="req">*</span>
              </label>
              <input
                id="bps-zip"
                className={`bp-input${err('zip') ? ' invalid' : ''}`}
                value={formatZipDisplay(zipDigitsFromInput(form.zip))}
                maxLength={10}
                disabled={readOnly}
                onChange={(e) => set('zip', zipDigitsFromInput(e.target.value))}
              />
              {err('zip') && <div className="error">{err('zip')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-county">County</label>
              <input id="bps-county" className="bp-input" value={form.county} maxLength={60} placeholder="Optional" disabled={readOnly} onChange={(e) => set('county', e.target.value)} />
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-country">
                Country <span className="req">*</span>
              </label>
              <select id="bps-country" className={`bp-select${err('country') ? ' invalid' : ''}`} value={form.country} disabled={readOnly} onChange={(e) => set('country', e.target.value)}>
                <option value="US">United States</option>
              </select>
            </div>
          </div>

          {/* EPN-37 correction: the single control for this section — View mode shows it disabled (matching
              every other read-only field's treatment), Edit mode makes it editable. Unchecking it here
              immediately reveals the Mailing Address tab above without navigating there automatically or
              saving anything; nothing commits until the page-level Save Changes confirmation. */}
          <label className="owner-check" style={{ marginTop: 20 }}>
            <input
              type="checkbox"
              checked={form.mailingSameAsBilling}
              disabled={readOnly}
              onChange={(e) => set('mailingSameAsBilling', e.target.checked)}
            />
            Mailing address is the same as billing address
          </label>
        </div>
      )}

      {tab === 'mailing' && !form.mailingSameAsBilling && (
        <div>
          <label className="owner-check" style={{ marginTop: 0, marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={form.mailingSameAsBilling}
              disabled={readOnly}
              onChange={(e) => {
                set('mailingSameAsBilling', e.target.checked);
                if (e.target.checked) setTab('billing');
              }}
            />
            Mailing Address: Same as Billing Address
          </label>
          <div className="bp-grid">
            <div className="bp-form-group span-2">
              <label htmlFor="bps-mailAddress1">
                Address Line 1 <span className="req">*</span>
              </label>
              <input
                id="bps-mailAddress1"
                className={`bp-input${err('mailingAddressLine1') ? ' invalid' : ''}`}
                value={form.mailingAddressLine1}
                maxLength={100}
                disabled={readOnly}
                onChange={(e) => set('mailingAddressLine1', e.target.value)}
              />
              {err('mailingAddressLine1') && <div className="error">{err('mailingAddressLine1')}</div>}
            </div>
            <div className="bp-form-group span-2">
              <label htmlFor="bps-mailAddress2">Address Line 2</label>
              <input id="bps-mailAddress2" className="bp-input" value={form.mailingAddressLine2} maxLength={100} disabled={readOnly} onChange={(e) => set('mailingAddressLine2', e.target.value)} />
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-mailCity">
                City <span className="req">*</span>
              </label>
              <input id="bps-mailCity" className={`bp-input${err('mailingCity') ? ' invalid' : ''}`} value={form.mailingCity} maxLength={60} disabled={readOnly} onChange={(e) => set('mailingCity', e.target.value)} />
              {err('mailingCity') && <div className="error">{err('mailingCity')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-mailState">
                State <span className="req">*</span>
              </label>
              <select id="bps-mailState" className={`bp-select${err('mailingState') ? ' invalid' : ''}`} value={form.mailingState} disabled={readOnly} onChange={(e) => set('mailingState', e.target.value)}>
                <option value="">-- Select --</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {err('mailingState') && <div className="error">{err('mailingState')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-mailZip">
                ZIP Code <span className="req">*</span>
              </label>
              <input
                id="bps-mailZip"
                className={`bp-input${err('mailingZip') ? ' invalid' : ''}`}
                value={formatZipDisplay(zipDigitsFromInput(form.mailingZip))}
                maxLength={10}
                disabled={readOnly}
                onChange={(e) => set('mailingZip', zipDigitsFromInput(e.target.value))}
              />
              {err('mailingZip') && <div className="error">{err('mailingZip')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-mailCounty">County</label>
              <input id="bps-mailCounty" className="bp-input" value={form.mailingCounty} maxLength={60} placeholder="Optional" disabled={readOnly} onChange={(e) => set('mailingCounty', e.target.value)} />
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-mailCountry">
                Country <span className="req">*</span>
              </label>
              <select id="bps-mailCountry" className="bp-select" value={form.mailingCountry} disabled={readOnly} onChange={(e) => set('mailingCountry', e.target.value)}>
                <option value="US">United States</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {apiError && <div className="notice" style={{ color: 'var(--bps-danger)' }}>{apiError}</div>}
    </div>
  );
});
